/**
 * OverviewTab
 * Role-aware overview tab showing role-specific widgets
 */

import React from 'react';
import { useAuth } from '../../../context/AuthContext';
import SystemStatusWidget from '../widgets/SystemStatusWidget';
import { isRoleInCategory } from '../config/dashboardConfig';

export default function OverviewTab() {
  const { user } = useAuth();
  const role = user?.role;

  return (
    <div>
      {/* Role-specific content */}
      {isRoleInCategory(role, 'SALES') && (
        <SalesOverview role={role} />
      )}

      {isRoleInCategory(role, 'DESIGN_LEAD') && (
        <DesignLeadOverview />
      )}

      {isRoleInCategory(role, 'DESIGN_WORKER') && (
        <DesignWorkerOverview />
      )}

      {isRoleInCategory(role, 'LEGAL') && (
        <LegalOverview />
      )}

      {isRoleInCategory(role, 'SITE_TEAM') && (
        <SiteTeamOverview />
      )}
    </div>
  );
}

/**
 * Sales Overview (Manager, Leader, Agent, Telemarketer)
 * Manager only: Shows System Status Widget
 * Other sales roles: Overview tab is now empty (widgets moved to their respective tabs)
 */
function SalesOverview({ role }) {
  // Only manager has content in overview now
  if (role !== 'manager') {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to your Dashboard</h3>
        <p className="text-gray-600">Use the tabs above to navigate to your projects and leads.</p>
      </div>
    );
  }

  return (
    <>
      {/* Manager only: System Status */}
      <SystemStatusWidget />
    </>
  );
}

/**
 * Design Lead Overview (SID, ASID)
 */
function DesignLeadOverview() {
  return (
    <div className="text-center py-12">
      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to your Dashboard</h3>
      <p className="text-gray-600">Use the tabs above to view your projects and measurements.</p>
    </div>
  );
}

/**
 * Design Worker Overview (Designer, Draughter_2D, Quotation Team)
 * No widgets - redirects user to use tabs
 */
function DesignWorkerOverview() {
  return (
    <div className="text-center py-12">
      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to your Dashboard</h3>
      <p className="text-gray-600">Use the tabs above to view your tasks and projects.</p>
    </div>
  );
}

/**
 * Legal Overview
 * No widgets - redirects user to use tabs
 */
function LegalOverview() {
  return (
    <div className="text-center py-12">
      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to your Dashboard</h3>
      <p className="text-gray-600">Use the tabs above to view projects.</p>
    </div>
  );
}

/**
 * Site Team Overview (Site Supervisor, Site Manager, Operation Head)
 * Welcome message directing to site database and attendance tracker
 */
function SiteTeamOverview() {
  return (
    <div className="text-center py-12">
      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Welcome to Site Management</h3>
      <p className="text-gray-600">Use the tabs above to view the Site Database and track attendance.</p>
    </div>
  );
}
